/**
 * Tileable noise primitives used by AssetForge. Everything here wraps on a period
 * so generated textures tile seamlessly — a visible seam on a wall reads as amateur
 * work immediately, so tiling is non-negotiable.
 */

function hash2(x, y, seed) {
  let h = x * 374761393 + y * 668265263 + seed * 2246822519;
  h = (h ^ (h >>> 13)) * 1274126177;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
const lerp = (a, b, t) => a + (b - a) * t;

/** Value noise on an integer lattice of `period` cells, wrapping at the period. */
export function valueNoise(x, y, period, seed) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const x0 = ((xi % period) + period) % period;
  const y0 = ((yi % period) + period) % period;
  const x1 = (x0 + 1) % period;
  const y1 = (y0 + 1) % period;
  const u = fade(xf);
  const v = fade(yf);
  return lerp(
    lerp(hash2(x0, y0, seed), hash2(x1, y0, seed), u),
    lerp(hash2(x0, y1, seed), hash2(x1, y1, seed), u),
    v
  );
}

/** Fractal Brownian motion. `period` is in lattice cells at the base octave. */
export function fbm(x, y, period, octaves, seed, gain = 0.5, lacunarity = 2) {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let p = period;
  let f = 1;
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise(x * f, y * f, p, seed + i * 1013) * amp;
    norm += amp;
    amp *= gain;
    f *= lacunarity;
    p = Math.round(p * lacunarity);
  }
  return sum / norm;
}

/** Ridged fbm — sharp creases, good for rock, cracked concrete, rust fronts. */
export function ridged(x, y, period, octaves, seed) {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let p = period;
  let f = 1;
  for (let i = 0; i < octaves; i++) {
    const n = 1 - Math.abs(valueNoise(x * f, y * f, p, seed + i * 7717) * 2 - 1);
    sum += n * n * amp;
    norm += amp;
    amp *= 0.5;
    f *= 2;
    p = Math.round(p * 2);
  }
  return sum / norm;
}

/**
 * Tiling Worley/cellular noise. Returns { f1, f2, id } — f2-f1 gives clean cell
 * borders for tile grout, cracked mud, and chipped paint edges.
 */
export function worley(x, y, period, seed) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  let f1 = 1e9;
  let f2 = 1e9;
  let id = 0;
  for (let oy = -1; oy <= 1; oy++) {
    for (let ox = -1; ox <= 1; ox++) {
      const cx = xi + ox;
      const cy = yi + oy;
      const wx = ((cx % period) + period) % period;
      const wy = ((cy % period) + period) % period;
      const px = cx + hash2(wx, wy, seed);
      const py = cy + hash2(wx, wy, seed + 4321);
      const d = Math.hypot(px - x, py - y);
      if (d < f1) {
        f2 = f1;
        f1 = d;
        id = hash2(wx, wy, seed + 99);
      } else if (d < f2) {
        f2 = d;
      }
    }
  }
  return { f1, f2, id };
}

/** Directional streak filter — grime running down a wall, brushed metal. */
export function streak(x, y, period, seed, length = 8) {
  let sum = 0;
  let w = 0;
  for (let i = 0; i < length; i++) {
    const a = 1 - i / length;
    sum += valueNoise(x, y - i * 0.35, period, seed) * a;
    w += a;
  }
  return sum / w;
}

export const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
export const smoothstep = (e0, e1, x) => {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
};
export { lerp };
